import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Skeleton, Table, Tag, Tooltip } from 'antd';
import DataTable from '@/components/data-table';
import TruncateTooltipCell from '@/components/data-table/TruncateTooltipCell';
import { Sidebar } from '@/components/sidebar';
import { API } from '@/services/api.service';
import { Facts, ListToolbar, Sums } from './Filters';
import { columnKeysOf, date, listQueryOf, money, noop } from './helpers';
import useColumnHeaders from './useColumnHeaders';

const DUTY_COLORS = { FullyPaid: 'green', PartiallyPaid: 'gold', NotPaid: 'red', NoDuties: 'default' };
const FILTERS = ['declarationNumber', 'customsInvoiceNumber', 'documentCode', 'customsDate', 'country', 'duties', 'status', 'lastPaymentDate'];
const SORTS = ['declarationNumber', 'customsInvoiceNumber', 'documentCode', 'customsDate', 'country', 'goodsValue', 'vatBase', 'vat', 'customsDuty', 'duties', 'totalPaid', 'remainingVat', 'lastPaymentDate', 'status'];
const KEYS = columnKeysOf(FILTERS, SORTS);
const EMPTY = '—';

/** Exempt import: nothing due, so its zero amounts show as empty, not as 0,00. */
const isExempt = (r) => r?.duties === 'NoDuties';
const amountOf = (r, v) => (isExempt(r) && !Number(v) ? EMPTY : money(v));

/** The payment tag; an exempt import names its exemption code instead of "no duties". */
function DutyTag({ row, t }) {
  const codes = row.exemptionCodes || [];
  const label = t(`customs.duties.${row.duties}`, { defaultValue: row.duties });
  if (!isExempt(row) || !codes.length) return <Tag color={DUTY_COLORS[row.duties] || 'default'}>{label}</Tag>;
  const text = `${t('customs.exempt')} · ${codes.join(', ')}`;
  return (
    <Tooltip title={`${label} · ${codes.join(', ')}`}>
      <Tag color="blue" className="max-w-full truncate align-middle">
        {text}
      </Tag>
    </Tooltip>
  );
}

/**
 * Import customs declarations (JCI) as SEF holds them from the Customs
 * Administration: VAT, customs duty, what was paid and what is left. A row
 * opens the declaration.
 */
export default function CustomsTab({ company, queryParams, setQueryParams }) {
  const { t } = useTranslation('sefState');
  const [selected, setSelected] = useState(null);
  const query = listQueryOf(queryParams);
  const list = useQuery({
    queryKey: ['sefState', company, 'customs', query],
    queryFn: () => API.sefState.getCustoms(company, query),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const { data } = list;
  const h = useColumnHeaders(queryParams, setQueryParams, FILTERS);
  const c = (key) => t(`customs.columns.${key}`);
  const opts = (key, label = (v) => v) => (data?.options?.[key] || []).map((v) => ({ value: v, label: label(v) }));
  const dutyLabel = (v) => t(`customs.duties.${v}`, { defaultValue: v });

  const columns = [
    {
      title: h.text('declarationNumber', c('declarationNumber')),
      dataIndex: 'declarationNumber',
      width: 190,
      render: (v) => <span className="font-semibold">{v}</span>,
    },
    { title: h.options('documentCode', c('documentCode'), opts('documentCode')), dataIndex: 'documentCode', width: 110 },
    { title: h.text('customsInvoiceNumber', c('customsInvoiceNumber')), dataIndex: 'customsInvoiceNumber', width: 170 },
    { title: h.date('customsDate', c('customsDate')), dataIndex: 'customsDate', width: 130, render: date },
    {
      title: h.options('country', c('country'), opts('country')),
      dataIndex: 'originCountries',
      width: 110,
      render: (v) => <TruncateTooltipCell label={(v || []).join(', ')} />,
    },
    { title: h.sort('goodsValue', c('goodsValue')), dataIndex: 'goodsValue', width: 160, align: 'right', render: money },
    { title: h.sort('vatBase', c('vatBase')), dataIndex: 'vatBase', width: 150, align: 'right', render: (v, r) => amountOf(r, v) },
    {
      title: h.sort('vat', c('vat')),
      dataIndex: 'vat',
      width: 140,
      align: 'right',
      render: (v, r) =>
        isExempt(r) && !Number(v) ? (
          EMPTY
        ) : (
          <Tooltip title={`20%: ${money(r.vat20)} · 10%: ${money(r.vat10)}`}>
            <span>{money(v)}</span>
          </Tooltip>
        ),
    },
    { title: h.sort('customsDuty', c('customsDuty')), dataIndex: 'customsDuty', width: 120, align: 'right', render: (v, r) => amountOf(r, v) },
    {
      title: h.options('duties', c('duties'), opts('duties', dutyLabel)),
      dataIndex: 'duties',
      width: 240,
      render: (v, r) => <DutyTag row={r} t={t} />,
    },
    { title: h.sort('totalPaid', c('totalPaid')), dataIndex: 'totalPaid', width: 140, align: 'right', render: (v, r) => amountOf(r, v) },
    {
      title: h.sort('remainingVat', c('remainingVat')),
      dataIndex: 'remainingVat',
      width: 150,
      align: 'right',
      render: (v, r) => <span className={v > 0 ? 'font-semibold text-red-600' : ''}>{amountOf(r, v)}</span>,
    },
    { title: h.date('lastPaymentDate', c('lastPaymentDate')), dataIndex: 'lastPaymentDate', width: 140, render: date },
    { title: h.options('status', c('status'), opts('status')), dataIndex: 'status', width: 120 },
  ];

  const labels = Object.fromEntries(
    ['declarationNumber', 'customsInvoiceNumber', 'customsDate', 'country', 'duties', 'exemptionCodes', 'goodsValue', 'vatBase', 'vat', 'customsDuty', 'excise', 'totalPaid', 'remainingVat', 'lastPaymentDate', 'status'].map(
      (k) => [k, c(k)]
    )
  );

  return (
    <>
      <DataTable
        rowKey="_id"
        title={`${t('customs.title')}${data ? ` · ${data.count}` : ''}`}
        data={data?.items || []}
        total={data?.count || 0}
        status={list.isLoading ? 'pending' : list.error ? 'error' : 'success'}
        columns={columns}
        offset={queryParams.offset}
        limit={queryParams.limit}
        onChangePagination={noop}
        onRow={(r) => ({ onClick: () => setSelected(r._id), className: 'cursor-pointer' })}
        tableLayout="fixed"
        scroll={{ x: columns.reduce((s, col) => s + (col.width || 0), 0) }}
        size="small"
        headerChildren={
          <ListToolbar
            keys={KEYS}
            setQueryParams={setQueryParams}
            exportProps={{ company, kind: 'customs', filters: query, labels, disabled: !data?.count }}
          />
        }
      />
      {data && (
        <Sums
          items={[
            [t('customs.sums.vat'), money(data.sums.vat)],
            [t('customs.sums.customsDuty'), money(data.sums.customsDuty)],
            [t('customs.sums.remainingVat'), money(data.sums.remainingVat)],
          ]}
        />
      )}
      <CustomsDetail company={company} id={selected} onClose={() => setSelected(null)} />
    </>
  );
}

/** One declaration: identification, amounts, duties by type, payments, history of changes. */
function CustomsDetail({ company, id, onClose }) {
  const { t } = useTranslation('sefState');
  const { data: d, isLoading, error } = useQuery({
    queryKey: ['sefState', company, 'customs', 'one', id],
    queryFn: () => API.sefState.getCustomsOne(company, id),
    enabled: Boolean(id),
  });
  const c = (key) => t(`customs.columns.${key}`);
  const dl = (key) => t(`customs.detail.${key}`);

  return (
    <Sidebar title={d?.declarationNumber || t('customs.detail.title')} open={Boolean(id)} onClose={onClose} className="max-w-2xl">
      <div className="grid gap-4 p-4">
        {isLoading && <Skeleton active paragraph={{ rows: 12 }} />}
        {error && <Alert type="error" showIcon message={t('errors.load')} />}
        {d && (
          <>
            <Facts
              items={[
                [c('declarationNumber'), `${d.declarationNumber} · ${d.documentCode}${d.versionsAvailable?.length > 1 ? ` · v${d.version} (${d.versionsAvailable.join(', ')})` : ''}`],
                [c('customsInvoiceNumber'), d.customsInvoiceNumber],
                [c('status'), d.status],
                [c('duties'), <Tag key="d" color={DUTY_COLORS[d.duties] || 'default'}>{t(`customs.duties.${d.duties}`, { defaultValue: d.duties })}</Tag>],
                [
                  c('exemptionCodes'),
                  (d.exemptions?.length ? d.exemptions : (d.exemptionCodes || []).map((code) => ({ code })))
                    .map((x) => (x.base == null ? x.code : `${x.code} · ${dl('exemptionBase')} ${money(x.base)}`))
                    .join('; ') || EMPTY,
                ],
                [dl('acceptanceDate'), date(d.acceptanceDate)],
                [c('customsDate'), date(d.customsDate)],
                [c('country'), (d.originCountries || []).join(', ')],
                [dl('declarant'), d.taxIdDeclarant],
                [dl('itemCount'), d.itemCount],
              ]}
            />
            <Facts
              items={[
                [c('goodsValue'), money(d.goodsValue)],
                [c('vatBase'), amountOf(d, d.vatBase)],
                [c('vat20'), amountOf(d, d.vat20)],
                [c('vat10'), amountOf(d, d.vat10)],
                [c('customsDuty'), amountOf(d, d.customsDuty)],
                [c('excise'), amountOf(d, d.excise)],
                [dl('totalToPay'), amountOf(d, d.totalToPay)],
                [c('totalPaid'), amountOf(d, d.totalPaid)],
                [c('remainingVat'), <span key="r" className={d.remainingVat > 0 ? 'font-semibold text-red-600' : ''}>{amountOf(d, d.remainingVat)}</span>],
              ]}
            />
            {d.duty?.length > 0 && (
              <div>
                <h4 className="mb-2 font-semibold">{dl('duty')}</h4>
                <Table
                  size="small"
                  rowKey={(r, i) => `${r.type}${i}`}
                  pagination={false}
                  dataSource={d.duty}
                  columns={[
                    { title: dl('dutyType'), dataIndex: 'type' },
                    { title: c('vatBase'), dataIndex: 'base', align: 'right', render: money },
                    { title: dl('rate'), dataIndex: 'rate', align: 'right', render: (v) => (v == null ? '' : `${v}%`) },
                    { title: dl('amount'), dataIndex: 'value', align: 'right', render: money },
                  ]}
                />
              </div>
            )}
            <div>
              <h4 className="mb-2 font-semibold">{t('customs.payments')}</h4>
              <Table
                size="small"
                rowKey={(r, i) => i}
                pagination={false}
                dataSource={d.payments}
                locale={{ emptyText: dl('noPayments') }}
                columns={[
                  { title: c('lastPaymentDate'), dataIndex: 'date', render: date },
                  { title: c('vat'), dataIndex: 'vat', align: 'right', render: money },
                  { title: c('customsDuty'), dataIndex: 'customs', align: 'right', render: money },
                  { title: dl('amount'), dataIndex: 'total', align: 'right', render: money },
                ]}
              />
            </div>
            {d.changes?.length > 0 && (
              <div>
                <h4 className="mb-2 font-semibold">{dl('changes')}</h4>
                <Table
                  size="small"
                  rowKey={(r, i) => i}
                  pagination={false}
                  dataSource={[...d.changes].reverse()}
                  columns={[
                    { title: dl('changedAt'), dataIndex: 'at', render: date },
                    { title: dl('field'), dataIndex: 'field', render: (f) => c(f) },
                    { title: dl('from'), dataIndex: 'from', render: (v) => (typeof v === 'number' ? money(v) : String(v ?? '')) },
                    { title: dl('to'), dataIndex: 'to', render: (v) => (typeof v === 'number' ? money(v) : String(v ?? '')) },
                  ]}
                />
              </div>
            )}
            <p className="text-xs opacity-60">
              {dl('fetchedAt')} {date(d.fetchedAt)} · SEF Id {d.sefId}
            </p>
          </>
        )}
      </div>
    </Sidebar>
  );
}
